import { createContext, ReactNode } from "react";
import {
    useMutation,
    UseMutationOptions,
    UseMutationResult,
    useQuery,
    UseQueryOptions,
    UseQueryResult,
} from "@tanstack/react-query";
import axios, { AxiosInstance, AxiosRequestConfig, isAxiosError } from "axios";
import { addToast } from "@heroui/react";

type RequestContextType = {
    axios: AxiosInstance;
    axiosRequest: <T>(
        config: AxiosRequestConfig,
        options?: {
            ignoreRedirect?: boolean;
        },
    ) => Promise<T>;
    useGetRequest: <T>({
        url,
        params,
        enabled,
        queryOptions,
    }: {
        url: string;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        params?: { [x: string]: any };
        enabled?: boolean;
        errorMessage?: string;
        successMessage?: string;
        queryOptions?: Partial<UseQueryOptions<T, Error>>;
    } & AxiosRequestConfig) => UseQueryResult<T>;
    useMutateRequest: <T>({
        method,
        url,
        errorMessage,
        successMessage,
        onSuccess,
        mutationOptions,
    }: {
        url?: string;
        method?: "post" | "patch" | "delete" | "get" | "put";
        errorMessage?: string;
        successMessage?: string;
        onSuccess?: (responseData: T) => void;
        mutationOptions?: Partial<
            UseMutationOptions<T, Error, AxiosRequestConfig>
        >;
    }) => UseMutationResult<T, Error, AxiosRequestConfig, unknown>;
    downloadFile: (url: string, fileName: string) => Promise<void>;
};

const RequestContext = createContext<RequestContextType | null>(null);

function getHostName() {
    if (window.location.hostname == "localhost") {
        return "/api/";
    }
    if (window.location.hostname == "ntime-dev.sentinelfertigation.com") {
        return "https://ntimeapi-dev.sentinelfertigation.com/";
    }
    if (window.location.hostname == "ntime.sentinelfertigation.com") {
        return "https://ntimeapi.sentinelfertigation.com/";
    }
    if (window.location.hostname == "grow-dev.sentinelag.tech") {
        return "https://grow-dev-api.sentinelag.tech/";
    }
    if (window.location.hostname == "grow.sentinelag.tech") {
        return "https://grow-api.sentinelag.tech/";
    }
    return "/api/";
}

const axiosInstance = axios.create({
    baseURL: getHostName(),
    withCredentials: true,
});

axiosInstance.interceptors.request.use((config) => {
    const token = window.localStorage.getItem("auth_token");
    if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    const timeTravel = sessionStorage.getItem("time-travel");
    if (
        timeTravel &&
        [
            "/api/",
            "http://localhost:8000/",
            "http://localhost:4200/",
            "https://ntimeapi-dev.sentinelfertigation.com/",
            "https://ntimeapi.sentinelfertigation.com/",
            "https://grow-dev-api.sentinelag.tech/",
            "https://grow-api.sentinelag.tech/",
        ].some((apiUrl) => config.baseURL?.includes(apiUrl))
    ) {
        config.headers["Time-Travel"] = timeTravel;
    }

    const unitHeader = localStorage.getItem("unit_preference");
    if (
        unitHeader &&
        [
            "/api/",
            "http://localhost:8000/",
            "http://localhost:4200/",
            "https://ntimeapi-dev.sentinelfertigation.com/",
            "https://ntimeapi.sentinelfertigation.com/",
            "https://grow-dev-api.sentinelag.tech/",
            "https://grow-api.sentinelag.tech/",
        ].some((apiUrl) => config.baseURL?.includes(apiUrl))
    ) {
        config.headers["N-Time-Unit-Type"] = unitHeader;
    }

    return config;
});

async function axiosRequest<T>(
    config: AxiosRequestConfig,
    {
        ignoreRedirect = false,
    }: {
        ignoreRedirect?: boolean;
    } = {},
): Promise<T> {
    try {
        const { data } = await axiosInstance<T>(config);
        return data;
    } catch (error) {
        if (isAxiosError(error)) {
            if (error.status == 401) {
                window.localStorage.removeItem("auth_token");
                window.localStorage.removeItem("refresh_token");
                if (!ignoreRedirect) {
                    if (window.localStorage.getItem("oldNTime") === "false") {
                        const currentUrl =
                            window.location.pathname.replace("/ntimev1", "") +
                            window.location.search;
                        window.location.href = `/auth?redirect=${currentUrl}`;
                    } else {
                        const currentUrl =
                            window.location.pathname.replace("/ntimev1", "") +
                            window.location.search;
                        window.location.href = `/ntimev1/auth?redirect=${currentUrl}`;
                    }
                }
            }
            if (error.response?.status === 400) {
                throw new Error(JSON.stringify(error.response?.data));
            }
            throw new Error(error.response?.data?.message || error.message);
        }
        throw error;
    }
}

function useGetRequest<T>({
    url,
    params = undefined,
    enabled = true,
    queryOptions,
    ...props
}: {
    url: string;
    params?: { [x: string]: string | number };
    enabled?: boolean;
    queryOptions?: Partial<UseQueryOptions<T, Error>>;
} & AxiosRequestConfig) {
    return useQuery<T>({
        ...(queryOptions ?? {}),
        queryKey: [url, ...(params ? Object.entries(params).flat() : [])],
        enabled: enabled,
        queryFn: () =>
            axiosRequest<T>({
                url,
                params,
                ...props,
            }),
    });
}

function useMutateRequest<T>({
    method,
    url,
    errorMessage,
    successMessage,
    onSuccess,
    mutationOptions,
}: {
    url?: string;
    method?: "post" | "patch" | "delete" | "get" | "put";
    errorMessage?: string;
    successMessage?: string;
    onSuccess?: (responseData: T) => void;
    mutationOptions?: Partial<UseMutationOptions<T, Error, AxiosRequestConfig>>;
}) {
    return useMutation<T, Error, AxiosRequestConfig>({
        ...(mutationOptions ?? {}),
        onSuccess: (response: T) => {
            if (successMessage) {
                addToast({
                    title: successMessage,
                    timeout: 3000,
                    color: "success",
                });
            }
            onSuccess?.(response);
        },
        onError: () => {
            if (errorMessage) {
                addToast({
                    title: errorMessage,
                    timeout: 3000,
                    color: "danger",
                });
            }
        },
        mutationFn: (config: AxiosRequestConfig) =>
            axiosRequest({
                url,
                method,
                ...config,
            }),
    });
}

async function downloadFile(url: string, fileName: string) {
    try {
        const { data } = await axiosInstance.get(url, {
            responseType: "blob",
        });

        const objectUrl = window.URL.createObjectURL(data);

        const fileLink = document.createElement("a");
        fileLink.href = objectUrl;

        fileLink.download = fileName;
        fileLink.click();
        document.body.removeChild(fileLink);
        window.URL.revokeObjectURL(objectUrl);
    } catch (error) {
        if (isAxiosError(error) && error.response?.status === 400) {
            const text = await error.response.data.text();
            throw new Error(text || "Error downloading file");
        }
    }
}

function RequestProvider({ children }: { children: ReactNode }) {
    return (
        <RequestContext.Provider
            value={{
                axios: axiosInstance,
                axiosRequest,
                useGetRequest,
                useMutateRequest,
                downloadFile,
            }}
        >
            {children}
        </RequestContext.Provider>
    );
}

export { RequestProvider, RequestContext };
export type { RequestContextType };
